import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IconButton, Stack, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../shared/contexts/app-context/components/AppContext";
import { OrderUpdateModel } from "../../../../screens/portfolio-info/types/OrderUpdateModel";
import {
    useActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { dateTimeConversionService } from "../../../../shared/services/DateTimeConversionService";
import { assetService } from "../../../../shared/services/AssetService";
import {
    ActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { AssetType } from "../../../../shared/types/AssetType";
import { OrderState } from "../../../../shared/types/OrderState";
import { DraggableDialogPaper } from "../DraggableDialogPaper";
import { DraggableDialogTitle } from "../DraggableDialogTitle";
import { ContractDetails, ContractDetailsState } from "./controls/ContractDetails";
import { PricingDetailsState, PricingParameters } from "./controls/PricingParameters";
import { OrderDetails, OrderDetailsState } from "./controls/OrderDetails";
import { BinanceOrderManager } from "./controls/BinanceOrderManager";
import { OptionDeltaTrend } from "./controls/OptionDeltaTrend";
import { oderManagerUtils } from "./utils/OrderManagerUtils";
import { AutoOrderManagerDialog } from "../auto-order-manager-dialog/AutoOrderManagerDialog";
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AutoOrderConfigurationModel } from "../../../../screens/portfolio-info/types/AutoOrderConfigurationModel";
import { AutoOrderConfigurationDto } from "../../../../screens/portfolio-info/types/AutoOrderConfigurationDto";
import { LocalStorageConstants } from "../../../../shared/constants/LocalStorageConstants";
import { DataProviders } from "../../../../shared/types/DataProviders";
import { useAuthContext } from "../../../../screens/authentication/hooks/use-auth/AuthContext";
import type { MarketDataDto } from "../../../../shared/types/MarketDataDto";
import { TargetPlatform } from "../../../../shared/types/TargetPlatform";
import { OrderProcessingType } from "../../../../shared/types/OrderProcessingType";
import { platformFeeCalculationService } from "../../../../shared/services/platform-fee-calculation-service/PlatformFeeCalculationService";

export function OrderManagerDialog({
    isOpen,
    setIsOpen,
    submitAction,
    deleteAction,
    editingModel,
    isNew,
    icon,
    onAutoOrderConfigurationSaved,
    saveAutoOrderConfiguration,
    fetchAutoOrderConfiguration,
    resetAutoOrderConfigurationState,
    reloadEditingOrder,
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    submitAction: ((portfolio: OrderUpdateModel) => Promise<void>) | undefined,
    deleteAction: () => void,
    editingModel: OrderUpdateModel | null,
    isNew: boolean,
    icon: React.ReactNode;
    onAutoOrderConfigurationSaved: (configuration: AutoOrderConfigurationModel) => void;
    saveAutoOrderConfiguration: (
        orderId: number,
        autoOrderConfiguration: AutoOrderConfigurationDto,
    ) => Promise<AutoOrderConfigurationModel | null>;
    fetchAutoOrderConfiguration: (orderId: number) => Promise<AutoOrderConfigurationModel | null>;
    resetAutoOrderConfigurationState: (orderId: number) => Promise<AutoOrderConfigurationModel | null>;
    reloadEditingOrder: (marketData: MarketDataDto | null) => Promise<OrderUpdateModel | null>;
}) {
    const {
        marketDataDto,
        fetchMarketData,
        setSelectedDataProvider,
    } = useAppContext();

    const { isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    const [isAutoOrderManagerDialogOpen, setIsAutoOrderManagerDialogOpen] = useState<boolean>(false);
    const [isDataUpdating, setIsDataUpdating] = useState<boolean>(false);
    
    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();
    
    const [contractDetails, setContractDetails] = useState<ContractDetailsState>({
        assetType: AssetType.Spot,
        expiration: '',
        strike: null,
        symbol: '',
    });

    const isInitializing = useRef(false);
    const [feeNeedsRecalculation, setFeeNeedsRecalculation] = useState(false);

    const handleContractDetailsChange = (patch: Partial<ContractDetailsState>) => {
        setContractDetails(prev => ({ ...prev, ...patch }));
        if (!isInitializing.current) {
            setFeeNeedsRecalculation(true);
        }
    };

    const [pricingDetails, setPricingDetails] = useState<PricingDetailsState>({
        dateForPricing: dayjs(),
        impliedVolatilityForPricing: 0,
        underlyingPriceForPricing: 0,
        theoreticalPrice: 0,
        delta: 0,
        gamma: 0,
        vega: 0,
        theta: 0,
    });

    const handlePricingDetailsChange = (patch: Partial<PricingDetailsState>) => {
        setPricingDetails(prev => ({ ...prev, ...patch }));
    };
    
    const [isFeeValid, setIsFeeValid] = useState(true);
    const [feeCalculationError, setFeeCalculationError] = useState(false);
    const [orderDetails, setOrderDetails] = useState<OrderDetailsState>({
        externalId: 0,
        state: OrderState.Disabled,
        targetPlatform: TargetPlatform.None,
        executedAt: dayjs(dateTimeConversionService.getCurrentUtcDate()),
        underlyingPriceAtExecution: 0,
        impliedVolatilityAtExecution: 0,
        exchangeOrderId: null,
        comment: null,
        price: 0,
        size: 0,
        volume: 0,
        fee: 0,
        processingType: OrderProcessingType.Taker,
        delta: 0,
        gamma: 0,
        vega: 0,
        theta: 0,
    });

    const handleOrderDetailsChange = (patch: Partial<OrderDetailsState>) => {
        setOrderDetails(prev => ({ ...prev, ...patch }));
        if (!isInitializing.current &&
            ['targetPlatform', 'underlyingPriceAtExecution', 'price', 'size', 'processingType']
                .some(property => property in patch)) {
            setFeeNeedsRecalculation(true);
        }
    };

    useEffect(() => {
        if (!feeNeedsRecalculation) {
            return;
        }

        if (orderDetails.processingType === OrderProcessingType.Manual ||
            !platformFeeCalculationService.supports(orderDetails.targetPlatform)) {
            setIsFeeValid(true);
            setFeeCalculationError(false);
            setFeeNeedsRecalculation(false);
            return;
        }

        const fee = platformFeeCalculationService.calculate({
            targetPlatform: orderDetails.targetPlatform,
            assetType: contractDetails.assetType,
            processingType: orderDetails.processingType,
            size: orderDetails.size,
            price: orderDetails.price,
            underlyingPrice: assetService.isOptionType(contractDetails.assetType)
                ? orderDetails.underlyingPriceAtExecution
                : null,
        });

        if (fee === null) {
            setIsFeeValid(false);
            setFeeCalculationError(true);
        } else {
            setOrderDetails(previous => ({ ...previous, fee }));
            setIsFeeValid(true);
            setFeeCalculationError(false);
        }
        setFeeNeedsRecalculation(false);
    }, [
        feeNeedsRecalculation,
        contractDetails.assetType,
        orderDetails.targetPlatform,
        orderDetails.processingType,
        orderDetails.size,
        orderDetails.price,
        orderDetails.underlyingPriceAtExecution,
    ]);
    
    useEffect(() => {
        if (editingModel === null || marketDataDto === undefined) {
            return;
        }

        const size = editingModel.size;
        isInitializing.current = true;
        
        if (assetService.isOptionType(editingModel.type)){
            const newContractDetails = {
                assetType: editingModel.type,
                expiration: dateTimeConversionService.convertDateToLongFormattedString(editingModel.expireAt),
                strike: editingModel.strike!,
                symbol: editingModel.symbol,
            };
            
            handleContractDetailsChange(newContractDetails);

            oderManagerUtils.updatePricingParameters(marketDataDto!, newContractDetails,
                dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                editingModel.impliedVolatilityForPricing ?? 0,
                editingModel.underlyingPriceForPricing ?? 0,
                handlePricingDetailsChange);
            
            handleOrderDetailsChange({
                externalId: editingModel.externalId,
                state: editingModel.state,
                targetPlatform: editingModel.targetPlatform,
                executedAt: dateTimeConversionService.convertToUtcDate(editingModel.openedPositionAt),
                impliedVolatilityAtExecution: editingModel.impliedVolatilityAtOpen ?? 0,
                underlyingPriceAtExecution: editingModel.underlyingPriceAtOpen ?? 0,
                exchangeOrderId: editingModel.exchangeOrderId,
                comment: editingModel.comment,
                price: editingModel.openPrice,
                size: size,
                volume: -editingModel.openPrice * size,
                fee: Math.abs(editingModel.fee ?? 0),
                processingType: editingModel.processingType ?? OrderProcessingType.Taker,
                delta: (editingModel.delta ?? 0) * size,
                gamma: (editingModel.gamma ?? 0) * size,
                vega: (editingModel.vega ?? 0) * size,
                theta: (editingModel.theta ?? 0) * size,
            });
        } else {
            const newContractDetails = {
                assetType: editingModel.type,
                expiration: '',
                strike: null,
                symbol: editingModel.symbol,
            };
            
            handleContractDetailsChange(newContractDetails);

            oderManagerUtils.updatePricingParameters(marketDataDto!, newContractDetails,
                dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                0, editingModel.underlyingPriceForPricing ?? 0, handlePricingDetailsChange);
            
            handlePricingDetailsChange({
                dateForPricing: dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                impliedVolatilityForPricing: 0,
                underlyingPriceForPricing: editingModel.underlyingPriceForPricing ?? 0,
            });
            
            handleOrderDetailsChange({
                externalId: editingModel.externalId,
                state: editingModel.state,
                targetPlatform: editingModel.targetPlatform,
                executedAt: dateTimeConversionService.convertToUtcDate(editingModel.openedPositionAt),
                underlyingPriceAtExecution: 0,
                impliedVolatilityAtExecution: 0,
                exchangeOrderId: editingModel.exchangeOrderId,
                comment: editingModel.comment,
                price: editingModel.openPrice,
                size: size,
                volume: -editingModel.openPrice * size,
                fee: Math.abs(editingModel.fee ?? 0),
                processingType: editingModel.processingType ?? OrderProcessingType.Taker,
                delta: 0,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
        isInitializing.current = false;
    }, [editingModel, marketDataDto]);
    
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleAutoOrderClick = (): void => {
        setIsAutoOrderManagerDialogOpen(true);
    };

    const fetchMarketInfo = async (): Promise<MarketDataDto | null> => {
        const selectedBaseAssetSymbol = localStorage.getItem(LocalStorageConstants.SelectedBaseAssetSymbol);
        const selectedExpiration = localStorage.getItem(LocalStorageConstants.SelectedExpirationName);

        return await fetchMarketData(selectedBaseAssetSymbol, selectedExpiration);
    }

    async function handleLoadExchangeData(dataProvider: DataProviders) {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        setIsDataUpdating(true);
        try {
            setSelectedDataProvider(dataProvider);
            const updatedMarketData = await fetchMarketInfo();
            await reloadEditingOrder(updatedMarketData);
        } finally {
            setIsDataUpdating(false);
        }
    }

    const currentOptionInfo = marketDataDto?.OptionInfos.find(option => option.Symbol === contractDetails.symbol)
        ?? null;

    const getCurrentOptionImpliedVolatility = (): number | null =>
        currentOptionInfo?.MarkImpliedVolatility ?? null;

    const handleSubmit = async () => {
        if (!isFeeValid || editingModel === null || marketDataDto === undefined || submitAction === undefined) {
            return;
        }

        const orderUpdateModel: OrderUpdateModel = {
            externalId: editingModel.externalId,
            internalId: editingModel.internalId,
            exchangeOrderId: orderDetails.exchangeOrderId,
            autoOrderConfiguration: editingModel.autoOrderConfiguration,
            state: orderDetails.state,
            targetPlatform: orderDetails.targetPlatform,
            type: contractDetails.assetType,
            strike: assetService.isOptionType(contractDetails.assetType)
                ? contractDetails.strike
                : null,
            expireAt: assetService.isOptionType(contractDetails.assetType)
                ? dateTimeConversionService.parseToUtcDate(contractDetails.expiration)
                : null,
            symbol: contractDetails.symbol,
            size: orderDetails.size,
            openPrice: orderDetails.price ?? 0,
            fee: orderDetails.fee === 0 ? 0 : -orderDetails.fee,
            processingType: orderDetails.processingType,
            markPrice: 0,
            dateForPricing: dateTimeConversionService.toUtcDate(pricingDetails.dateForPricing),
            impliedVolatilityForPricing: pricingDetails.impliedVolatilityForPricing,
            underlyingPriceForPricing: pricingDetails.underlyingPriceForPricing,
            openedPositionAt: dateTimeConversionService.toUtcDate(orderDetails.executedAt),
            underlyingPriceAtOpen: assetService.isOptionType(contractDetails.assetType)
                ? orderDetails.underlyingPriceAtExecution
                : null,
            impliedVolatilityAtOpen: assetService.isOptionType(contractDetails.assetType)
                ? orderDetails.impliedVolatilityAtExecution
                : null,
            delta: orderDetails.delta,
            gamma: null,
            vega: null,
            theta: null,
            comment: orderDetails.comment,
        };

        await submitAction(orderUpdateModel);
    }
    
    const handleSubmitAndCloseDialog = async (arg: React.FormEvent<HTMLFormElement>) => {
        arg.preventDefault();
        await handleSubmit();
        handleClose();
    };
    
    const handleDeleteClick = () => {
        setConfirmedAction(() => deleteAction());
        setConfirmationMessage("Are you sure you want to delete the item from portfolio?");
        setSubmitButtonName("Delete");
        setIsActionConfirmationDialogOpen(true);
    }
    
    const handleCommentChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        handleOrderDetailsChange({comment: arg.target.value});
    }

    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                confirmedParentAction={confirmedAction}
                submitButtonName={submitButtonName}
                doAfterConfirmation={handleClose}
            />
            <AutoOrderManagerDialog
                isOpen={isAutoOrderManagerDialogOpen}
                setIsOpen={setIsAutoOrderManagerDialogOpen}
                orderId={editingModel?.externalId ?? 0}
                optionOrderSymbol={contractDetails.symbol}
                optionOrderSize={orderDetails.size}
                optionImpliedVolatility={getCurrentOptionImpliedVolatility()}
                underlyingPrice={marketDataDto?.UnderlyingPrice ?? marketDataDto?.BaseAsset.Price ?? null}
                autoOrderConfiguration={editingModel?.autoOrderConfiguration ?? null}
                onConfigurationSaved={onAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                icon={<AutofpsSelectIcon />}
            />
            <Dialog
                open={isOpen}
                onClose={handleClose}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmitAndCloseDialog,
                }}
                maxWidth={false}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title={`${isNew ? "Add" : "Edit"} order: ${contractDetails.symbol}`}
                    onClose={handleClose}
                />
                <DialogContent sx={{paddingY: '11px', paddingX: '18px'}}>
                    <Stack direction="row" spacing={2}>
                        <Stack direction="column" spacing={1.7}>
                            <ContractDetails
                                contractDetails={contractDetails}
                                handleContractDetailsChange={handleContractDetailsChange}
                                orderDetails={orderDetails}
                                handleOrderDetailsChange={handleOrderDetailsChange}
                                handlePricingDetailsChange={handlePricingDetailsChange}
                            />
                            <PricingParameters
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                pricingDetails={pricingDetails}
                                handlePricingDetailsChange={handlePricingDetailsChange}
                            />
                            <OrderDetails
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                handleOrderDetailsChange={handleOrderDetailsChange}
                                automaticRatesAvailable={platformFeeCalculationService.supports(orderDetails.targetPlatform)}
                                feeCalculationError={feeCalculationError}
                                onProcessingTypeChange={processingType => {
                                    handleOrderDetailsChange({ processingType });
                                    setFeeCalculationError(false);
                                }}
                                onFeeValidityChange={setIsFeeValid}
                            />
                            <BinanceOrderManager
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                optionInfo={currentOptionInfo}
                                isOptionsOrderBookEnabled={
                                    marketDataDto?.ApplicationState.ApplicationSettings.FeatureFlags
                                        .EnableOptionsOrderBookConnection === true
                                }
                                handleOrderDetailsChange={handleOrderDetailsChange}
                                handleSubmit={handleSubmit}
                                onAutoOrderClick={handleAutoOrderClick}
                            />
                            <TextField
                                margin="normal"
                                id="comment"
                                name="comment"
                                label="Comment"
                                type="string"
                                size="small"
                                value={orderDetails.comment ?? ''}
                                onChange={handleCommentChanging}
                                fullWidth
                                multiline
                                rows={1}
                                variant="outlined"
                            />
                        </Stack>
                        {assetService.isOptionType(contractDetails.assetType) &&
                            <OptionDeltaTrend
                                contractDetails={contractDetails}
                                pricingDetails={pricingDetails}
                            />
                        }
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Tooltip
                        title="Update client data"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <span>
                            <IconButton
                                type="button"
                                color="primary"
                                onClick={async () => await handleLoadExchangeData(DataProviders.Binance)}
                                disabled={isDataUpdating}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Stack direction="row" spacing={1}>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            size="small"
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                                !isFeeValid ||
                                orderDetails.size === null ||
                                orderDetails.price === null ||
                                (
                                    assetService.isOptionType(contractDetails.assetType) &&
                                    (contractDetails.expiration === '' ||
                                        contractDetails.strike === null)
                                )
                            }
                        >
                            {isNew ? "Add" : "Update"}
                        </Button>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={handleDeleteClick}
                            disabled={
                                oderManagerUtils.isNewOrder(orderDetails) ||
                                oderManagerUtils.isOriginalOrderArchived(orderDetails) ||
                                oderManagerUtils.isOptionCanBeCancelled(orderDetails)
                            }
                        >
                            Delete
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
}
